import { Button, Dropdown, Input, Menu, Spin, Table, Tooltip } from 'antd'
import { Select } from 'antd/lib'
import { EditOutlined, EyeOutlined, FilterOutlined } from '@ant-design/icons'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ColumnsType } from 'antd/es/table'
import { Link } from 'react-router-dom'

import {
  ActionsCellContainer,
  ContentContainer,
  StyledRow,
  StyledSelect,
} from '../StyledComponents'
import { useUserResponseManager } from '../../hooks'
import { useGeneral, useUser } from '../../contexts'
import { useFiltersV2 } from '../../contexts/FiltersContextV2'
import { Carrier, PATHS, RoleEnum } from '../../types'
import { useCarriersView } from '../../hooks/useCarriersView'
import { AddCarrierModal } from '../../features/TplRouter/TplDashboard/components/AddCarrierModal'
import { EditCarrierModal } from '../../features/TplRouter/TplDashboard/components/EditCarrierModal'

const { Option } = Select

const setupRoutes = (role: RoleEnum.ADMIN | RoleEnum.ADMINRO) => ({
  selectedDrivers: {
    path: PATHS[role].DRIVERS,
    labelKey: 'action_column_selected.drivers',
    multiple: true,
  },
  selectedTrucks: {
    path: PATHS[role].VEHICLES,
    labelKey: 'action_column_selected.trucks',
    multiple: true,
  },
  selectedTrailers: {
    path: PATHS[role].TRAILERS,
    labelKey: 'action_column_selected.trailers',
    multiple: true,
  },
  selectedContracts: {
    path: PATHS[role].CONTRACTS,
    labelKey: 'action_column_selected.contracts',
    multiple: false,
  },
})

const CarriersView: FC = () => {
  const { t } = useTranslation()
  const { state: user } = useUser()
  const { filters, handleFilterChange } = useFiltersV2()
  const { filtersDisplayed, menuDisplayed, handleDisplayFilters } = useGeneral()
  const { carriers, isLoading } = useCarriersView({ filters })
  const { userIsLoading } = useUserResponseManager(user?.id)
  const [isAddCarrierModalOpen, setIsAddCarrierModalOpen] = useState(false)
  const [onEditCarrierModalOpen, setOnEditCarrierModalOpen] = useState(false)
  const [selectedCarrier, setSelectedCarrier] = useState<number>()
  const handleCloseAddCarrierModal = () => {
    setIsAddCarrierModalOpen(false)
  }

  const handleCloseEditCarrierModal = () => {
    setOnEditCarrierModalOpen(false)
  }

  const handleSearchText = (value?: string | null | undefined) => {
    if (value === '' || !value) {
      handleFilterChange('name')(undefined)
    } else {
      handleFilterChange('name')(value)
    }
  }

  const routes = setupRoutes(user!.roles[0].name as unknown as RoleEnum.ADMIN | RoleEnum.ADMINRO)

  const handleOpenEditCarrierModal = (carrierId: number) => {
    setSelectedCarrier(carrierId)
    setOnEditCarrierModalOpen(true)
  }

  const actionButtons = (record: Carrier) => {
    const actionColumnSelected = (
      <Menu>
        {Object.entries(routes).map(([key, routeCfg]) => (
          <Menu.Item key={key}>
            <Link
              to={`${routeCfg.path}?carrierId=${routeCfg.multiple ? `[${record.id}]` : record.id}`}
            >
              {t(routeCfg.labelKey)}
            </Link>
          </Menu.Item>
        ))}
      </Menu>
    )

    return (
      <ActionsCellContainer>
        <Dropdown overlay={actionColumnSelected} trigger={['click']}>
          <Button>
            <EyeOutlined />
          </Button>
        </Dropdown>
        {user?.roles[0].name === 'ADMIN' && (
          <Tooltip title={t('edit_carrier')}>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleOpenEditCarrierModal(record.id)}
            />
          </Tooltip>
        )}
      </ActionsCellContainer>
    )
  }

  const rowClassName = (_record: unknown, index: number) =>
    index % 2 === 0 ? 'zebra-even' : 'zebra-odd'

  const columnsCarriers: ColumnsType = [
    {
      title: t('tpl_dashboard.name'),
      dataIndex: 'tradeName',
      key: 'tradeName',
      render: (trade_name: string) => trade_name ?? '-',
    },
    {
      title: t('tpl_dashboard.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('tpl_dashboard.cellphone'),
      dataIndex: 'cellphone',
      key: 'cellphone',
    },
    {
      title: t('tpl_dashboard.sap_id'),
      dataIndex: 'sapId',
      key: 'sapId',
      render: (sap_id: string) => sap_id ?? '-',
    },
    {
      title: t('tpl_dashboard.status'),
      dataIndex: 'disableAt',
      key: 'disableAt',
      render: (disableAt: Date) =>
        disableAt === null ? (
          <span>{t('globals.generics.enabled')}</span>
        ) : (
          <span style={{ color: 'red' }}>{t('globals.generics.disabled')}</span>
        ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      align: 'center',
      className: 'column-width',
      render: actionButtons,
    },
  ]

  return (
    <ContentContainer>
      <StyledRow className="sticky-options">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <Tooltip
              placement="bottomRight"
              title={
                user?.roles[0].name === 'ADMIN' || user?.roles[0].name === 'PROCUREMENT'
                  ? t('carrier_modal.new_carrier')
                  : ''
              }
            >
              <Button
                className="inline"
                type="primary"
                onClick={() => setIsAddCarrierModalOpen(true)}
                disabled={
                  !(user?.roles[0].name === 'ADMIN' || user?.roles[0].name === 'PROCUREMENT')
                }
              >
                <p style={{ marginTop: 1, marginLeft: 1 }}>{t('carrier_modal.new_carrier')}</p>
              </Button>
            </Tooltip>
            <Input
              style={{ width: '25%', minWidth: '20rem' }}
              placeholder={t('tpl_dashboard.search_name')}
              type="string"
              onPressEnter={e => {
                handleSearchText(e.currentTarget.value)
              }}
              onBlur={e => {
                handleSearchText(e.currentTarget.value)
              }}
              onChange={e => {
                if (e.currentTarget.value === '' || !e.currentTarget.value) {
                  handleFilterChange('name')(undefined)
                }
              }}
              allowClear
            />
            <StyledSelect
              maxTagCount="responsive"
              allowClear
              value={filters.disabled}
              placeholder={t('tpl_dashboard.state')}
              onChange={handleFilterChange('disabled')}
            >
              <Option value={false}>{t('globals.generics.enabled')}</Option>
              <Option value={true}>{t('globals.generics.disabled')}</Option>
            </StyledSelect>
          </div>
        </div>
      </StyledRow>
      <StyledRow className="ticket-handler">
        <div style={{ flex: 1 }} />
        {userIsLoading ? <Spin /> : <div style={{ flex: 1 }} />}
      </StyledRow>
      <Table
        columns={columnsCarriers}
        dataSource={carriers?.carriers}
        loading={isLoading}
        rowKey="id"
        rowClassName={rowClassName}
        pagination={{
          pageSize: filters.limit as number,
          onChange: handleFilterChange('page', false),
          total: carriers?.total,
          showSizeChanger: false,
          current: filters.page as number,
        }}
      />
      <AddCarrierModal open={isAddCarrierModalOpen} onClose={handleCloseAddCarrierModal} />
      {selectedCarrier && carriers && (
        <EditCarrierModal
          isOpen={onEditCarrierModalOpen}
          onClose={handleCloseEditCarrierModal}
          selectedCarrier={selectedCarrier}
          carriersData={carriers?.carriers}
        />
      )}
    </ContentContainer>
  )
}

export default CarriersView
